/**
 * Browser tab titles — one string per route, printed from the SAME registry
 * the sidebar and TabShell are printed from (`NAV_GROUPS`), so a title can
 * never drift from the nav label a trader clicked (#781).
 *
 * Shape: `G1` first, then the section, then the page or game, joined by one
 * separator glyph:
 *   G1 · Value Dashboard              (single-page group)
 *   G1 · Organization · Exports       (multi-page group)
 *   G1 · MLB · BOS @ NYY              (an open game)
 *   G1 · Recent Fills                 (the popup window)
 *   G1                                (unknown / transitional route)
 *
 * A game titles off the row the Game Detail page RESOLVED, not off the URL,
 * because the URL is the weaker identity: the legacy sport-less link is still
 * minted by My Trades, Live Discovery, Position Tracker's Poly tables and
 * GameTable's fallback, so a URL-derived title would name the sport only when
 * the trader happened to arrive from the dashboard. The URL remains the
 * fallback for the frames before the row resolves, and for archived games
 * whose live rows hydra has pruned.
 */
import { NAV_GROUPS, pathMatches } from '../../types/navigation';
import { normalizeSport } from '../../stores/marketFrameNormalization';
import { SPORT_LABEL } from '../../views/ValueDashboard/types';
import type { OpenGameIdentity } from '../../stores/openGame';

export const TITLE_SEPARATOR = ' · ';
const deploymentLabel = import.meta.env.VITE_DEPLOYMENT_LABEL?.trim();
/** Explicit build identity; unrelated deployments retain their exact old titles. */
export const TITLE_PREFIX = deploymentLabel
  ? `G1${TITLE_SEPARATOR}${deploymentLabel}`
  : 'G1';

/** What the shell knows that the path doesn't. */
export interface TitleContext {
  /** The game the Game Detail page currently has resolved, if any. */
  game?: OpenGameIdentity | null;
}

/** Route of the detached Recent Fills window (App.tsx mounts it above the shell). */
const FILLS_POPUP_PATH = '/fills';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function title(...parts: string[]): string {
  return [TITLE_PREFIX, ...parts].join(TITLE_SEPARATOR);
}

/** The display label for a sport spelling, or null when we don't know it.
 *  Folded through `normalizeSport` first so hydra's vocabulary (`ncaaf`)
 *  reaches the same label as g1's (`cfb`). */
function sportLabel(sport: string | null | undefined): string | null {
  if (!sport) return null;
  const id = normalizeSport(sport) ?? normalizeSport(sport.toLowerCase());
  return id ? SPORT_LABEL[id] ?? null : null;
}

/** The URL identity of a `/game/...` route, or null when it isn't one. */
interface GameRoute {
  sport: string | null;
  away: string;
  home: string;
}

/**
 * Both route families parse here: the sport-aware
 * `/game/:sport/:date/:AWAY/:HOME(/:gameNo)(/sources)` and the legacy
 * sport-less `/game/:date/:AWAY/:HOME(/sources)`, told apart by whether the
 * first segment is a date. Segments are NOT percent-decoded: every minter
 * builds these from alphanumeric team codes, on which `encodeURIComponent` is
 * already a no-op, and decoding a hand-mangled URL throws — inside the shell's
 * effect that would blank the app rather than mistitle a tab.
 */
function parseGameRoute(pathname: string): GameRoute | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'game') return null;
  const rest = segments.slice(1);
  const sport = DATE_RE.test(rest[0] ?? '') ? null : rest.shift() ?? null;
  const [date, away, home] = rest;
  if (!date || !DATE_RE.test(date) || !away || !home) return null;
  return { sport, away: away.toUpperCase(), home: home.toUpperCase() };
}

/**
 * The resolved row for THIS route, or null.
 *
 * Guards against titling one game with another's row: during a navigation
 * between two games the URL changes a frame before the row does. Codes are
 * compared order-independently, since the whole point of preferring the row is
 * that a URL's order can't be trusted.
 */
function resolvedGameFor(route: GameRoute, game: OpenGameIdentity | null | undefined) {
  if (!game) return null;
  const urlPair = [route.away, route.home].sort();
  const rowPair = [game.awayCode.toUpperCase(), game.homeCode.toUpperCase()].sort();
  return urlPair[0] === rowPair[0] && urlPair[1] === rowPair[1] ? game : null;
}

function gameTitle(route: GameRoute, context: TitleContext): string {
  const resolved = resolvedGameFor(route, context.game);
  const teams = resolved
    ? `${resolved.awayCode.toUpperCase()} @ ${resolved.homeCode.toUpperCase()}`
    : `${route.away} @ ${route.home}`;
  const label = sportLabel(resolved ? resolved.sport : route.sport);
  return label ? title(label, teams) : title(teams);
}

/**
 * The tab title for `pathname`.
 *
 * Nav pages match by longest path first, so a page's own sub-routes (a trader
 * drill-down under Position Tracker, a nested G3 tab) inherit their section's
 * title instead of falling back. The registry is read unfiltered by role: a
 * route the user can't reach is redirected by its guard before this title is
 * ever painted, and role-filtering here would only make a title depend on who
 * is looking.
 */
export function titleForRoute(pathname: string, context: TitleContext = {}): string {
  if (pathMatches(pathname, FILLS_POPUP_PATH)) return title('Recent Fills');

  const route = parseGameRoute(pathname);
  if (route) return gameTitle(route, context);

  const matches = NAV_GROUPS.flatMap((group) =>
    group.pages
      .filter((page) => pathMatches(pathname, page.path))
      .map((page) => ({ group, page })),
  ).sort((a, b) => b.page.path.length - a.page.path.length);

  const hit = matches[0];
  if (!hit) return TITLE_PREFIX;
  // A single-page group's icon, tooltip, and page all carry the one label;
  // repeating it ("G1 · Settings · Settings") would say nothing twice.
  return hit.group.pages.length === 1
    ? title(hit.page.label)
    : title(hit.group.label, hit.page.label);
}
